import { el } from '../ui/dom';
import {
  CmsClient,
  type CmsAttempt,
  type CmsGame,
  type DashboardReport,
  type LeaderboardReport,
  type ReportFilters,
  type SchoolReport,
} from './client';
import {
  button,
  dateTime,
  duration,
  emptyState,
  errorMessage,
  openDialog,
  percent,
  setSelectOptions,
  statusBadge,
} from './ui';

function summaryCard(label: string, value: string, detail: string, modifier: string): HTMLElement {
  return el(
    'article',
    { class: `cms-summary-card ${modifier}` },
    el('p', { class: 'cms-summary-card__label', text: label }),
    el('strong', { class: 'cms-summary-card__value', text: value }),
    el('p', { class: 'cms-summary-card__detail', text: detail })
  );
}

function attemptCell(result: SchoolReport['rows'][number]['results'][string] | undefined): HTMLTableCellElement {
  const attempt = result?.bestAttempt ?? null;
  if (!attempt) {
    return el(
      'td',
      { class: 'cms-progress-cell cms-progress-cell--missing' },
      el('span', { class: 'cms-progress-cell__status', text: 'Not started' }),
      el('small', { text: `${result?.attemptCount ?? 0} attempts` })
    );
  }
  return el(
    'td',
    { class: 'cms-progress-cell cms-progress-cell--complete' },
    el('strong', { text: attempt.score.toLocaleString() }),
    el('span', { text: duration(attempt.durationMs) }),
    el('span', { class: 'cms-progress-cell__status', text: `${result?.attemptCount ?? 0} attempts` }),
    el('small', { text: result?.lastPlayedAt ? `Last ${dateTime(result.lastPlayedAt)}` : '' })
  );
}

function renderParticipation(dashboard: DashboardReport): HTMLElement {
  return el(
    'div',
    { class: 'cms-participation-strip' },
    ...dashboard.perGameParticipation.map((item) =>
      el(
        'article',
        {},
        el('strong', { text: item.game.name }),
        el('span', { text: `${item.participantCount} played · ${item.completedCount} completed` }),
        el(
          'div',
          { class: 'cms-progress-bar', attrs: { 'aria-label': `${percent(item.participationRate)} participation` } },
          el('span', { style: { width: percent(item.participationRate) } })
        )
      )
    )
  );
}

function renderLeaderboard(report: LeaderboardReport): HTMLElement {
  return el(
    'div',
    { class: 'cms-leaderboard-grid' },
    ...report.byGame.map((board) =>
      el(
        'article',
        { class: 'cms-leaderboard' },
        el('h3', { text: board.game.name }),
        board.entries.length
          ? el(
              'ol',
              {},
              ...board.entries.slice(0, 10).map((entry) =>
                el(
                  'li',
                  {},
                  el('span', { class: 'cms-rank', text: entry.rank }),
                  el(
                    'span',
                    { class: 'cms-leader-name' },
                    el('strong', { text: entry.student.studentName }),
                    el('small', { text: entry.student.className })
                  ),
                  el(
                    'span',
                    { class: 'cms-leader-score' },
                    el('strong', {
                      text:
                        report.metric === 'fastest'
                          ? duration(entry.attempt.durationMs)
                          : entry.attempt.score.toLocaleString(),
                    }),
                    el('small', {
                      text:
                        report.metric === 'fastest'
                          ? `${entry.attempt.score.toLocaleString()} pts`
                          : duration(entry.attempt.durationMs),
                    })
                  )
                )
              )
            )
          : el('p', { class: 'cms-leaderboard__empty', text: 'No completed attempts yet.' })
      )
    )
  );
}

function renderAttempts(attempts: readonly CmsAttempt[]): HTMLElement {
  if (!attempts.length) return emptyState('No attempts in this view.', 'Change the report filters to see more activity.');
  return el(
    'div',
    { class: 'cms-table-scroll' },
    el(
      'table',
      { class: 'cms-data-table' },
      el('thead', {}, el('tr', {}, ...['Game', 'Version', 'Score', 'Duration', 'Status', 'Played'].map((label) => el('th', { text: label })) )),
      el(
        'tbody',
        {},
        ...[...attempts]
          .sort((left, right) => right.completedAt.localeCompare(left.completedAt))
          .map((attempt) =>
            el(
              'tr',
              {},
              el('td', { text: attempt.gameId }),
              el('td', { text: attempt.gameVersion }),
              el('td', { text: attempt.score.toLocaleString() }),
              el('td', { text: duration(attempt.durationMs) }),
              el('td', {}, statusBadge(attempt.status)),
              el('td', { text: dateTime(attempt.completedAt) })
            )
          )
      )
    )
  );
}

export async function createDashboardView(
  client: CmsClient,
  schoolId: string,
  announce: (message: string, error?: boolean) => void
): Promise<HTMLElement> {
  let className = '';
  let gameId = '';
  let search = '';
  let completionStatus: ReportFilters['completionStatus'];
  let from = '';
  let to = '';
  let metric: 'highest' | 'fastest' = 'highest';
  let knownClasses: readonly string[] = [];
  let knownGames: readonly CmsGame[] = [];

  const summary = el('section', { class: 'cms-summary-grid', attrs: { 'aria-label': 'Progress summary' } });
  const participation = el('section', { class: 'cms-participation', attrs: { 'aria-label': 'Participation by game' } });
  const matrix = el('div', { class: 'cms-panel__body' });
  const leaderboards = el('div', { class: 'cms-panel__body' });
  const classSelect = el('select', { class: 'cms-select', attrs: { 'aria-label': 'Group' } });
  const gameSelect = el('select', { class: 'cms-select', attrs: { 'aria-label': 'Game' } });
  const completionSelect = el('select', { class: 'cms-select', attrs: { 'aria-label': 'Completion status' } });
  setSelectOptions(completionSelect, [
    { value: '', label: 'All progress' },
    { value: 'completed', label: 'Has completed' },
    { value: 'not_started', label: 'Not started' },
    { value: 'abandoned', label: 'Has abandoned' },
  ]);
  const searchInput = el('input', { class: 'cms-input', attrs: { type: 'search', placeholder: 'Search name, ID or group', 'aria-label': 'Search students' } });
  const fromInput = el('input', { class: 'cms-input', attrs: { type: 'date', 'aria-label': 'From date' } });
  const toInput = el('input', { class: 'cms-input', attrs: { type: 'date', 'aria-label': 'To date' } });
  const refreshButton = button('Refresh');
  const highButton = button('Highest score', 'primary');
  const fastButton = button('Fastest time', 'secondary');

  const filters = (): ReportFilters => ({
    schoolId,
    groupName: className || undefined,
    gameId: gameId || undefined,
    search: search || undefined,
    completionStatus,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  });

  const showStudent = async (studentRecordId: string): Promise<void> => {
    const modal = openDialog('Student attempt history', 'All attempts are retained; the best attempt follows score, time, then earliest completion.', true);
    modal.body.append(el('div', { class: 'cms-loading', text: 'Loading attempts…' }));
    try {
      const report = await client.studentReport(studentRecordId, filters());
      const attempts = report.games.flatMap((item) => item.attempts);
      modal.body.replaceChildren(
        el(
          'div',
          { class: 'cms-dialog__title-row' },
          el('div', {}, el('strong', { text: report.student.studentName }), el('span', { text: `${report.student.studentId} · ${report.student.className}` })),
          button('Export attempts', 'secondary', () => void client.downloadReport({ ...filters(), studentId: report.student.id }, 'student'))
        ),
        renderAttempts(attempts)
      );
    } catch (reason) {
      modal.body.replaceChildren(emptyState('Student report unavailable.', errorMessage(reason)));
    }
  };

  const renderMatrix = (report: SchoolReport): HTMLElement => {
    if (!report.rows.length) return emptyState('No students match these filters.', 'Import students or change the report filters.');
    return el(
      'div',
      { class: 'cms-table-scroll' },
      el(
        'table',
        { class: 'cms-progress-table' },
        el(
          'thead',
          {},
          el('tr', {}, el('th', { text: 'Student' }), el('th', { text: 'Group' }), ...report.games.map((game) => el('th', { text: game.name })))
        ),
        el(
          'tbody',
          {},
          ...report.rows.map((row) =>
            el(
              'tr',
              {},
              el(
                'th',
                {},
                el('button', {
                  class: 'cms-student-link',
                  attrs: { type: 'button' },
                  on: { click: () => void showStudent(row.student.id) },
                }, el('strong', { text: row.student.studentName }), el('span', { class: 'cms-student-id', text: row.student.studentId }))
              ),
              el('td', { text: row.student.className }),
              ...report.games.map((game) => attemptCell(row.results[game.id]))
            )
          )
        )
      )
    );
  };

  const refresh = async (resetOptions = false): Promise<void> => {
    refreshButton.disabled = true;
    announce('Loading school progress…');
    try {
      const [dashboard, report, leaderboard] = await Promise.all([
        client.dashboard(filters()),
        client.schoolReport(filters()),
        client.leaderboards(filters(), metric),
      ]);
      summary.replaceChildren(
        summaryCard('Students', String(dashboard.studentCount), 'Active learners in view', 'cms-summary-card--students'),
        summaryCard('Participating', String(dashboard.participantCount), 'Learners with an attempt', 'cms-summary-card--participants'),
        summaryCard('Completion', percent(dashboard.completionRate), `${dashboard.completedGameCount} game completions`, 'cms-summary-card--completion'),
        summaryCard('Attempts', String(dashboard.attemptCount), 'Completed and abandoned', 'cms-summary-card--attempts')
      );
      participation.replaceChildren(renderParticipation(dashboard));
      matrix.replaceChildren(renderMatrix(report));
      leaderboards.replaceChildren(renderLeaderboard(leaderboard));
      if (resetOptions || !knownClasses.length) knownClasses = [...new Set(report.rows.map((row) => row.student.className))].sort();
      if (resetOptions || !knownGames.length) knownGames = report.games;
      setSelectOptions(classSelect, [{ value: '', label: 'All groups' }, ...knownClasses.map((value) => ({ value, label: value }))], className);
      setSelectOptions(gameSelect, [{ value: '', label: 'All games' }, ...knownGames.map((game) => ({ value: game.id, label: game.name }))], gameId);
      announce(`Updated ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
      matrix.replaceChildren(emptyState('Progress unavailable.', errorMessage(reason)));
      leaderboards.replaceChildren();
    } finally {
      refreshButton.disabled = false;
    }
  };

  const changeFilter = (): void => {
    className = classSelect.value;
    gameId = gameSelect.value;
    completionStatus = completionSelect.value as ReportFilters['completionStatus'] || undefined;
    from = fromInput.value;
    to = toInput.value;
    void refresh();
  };
  classSelect.addEventListener('change', changeFilter);
  gameSelect.addEventListener('change', changeFilter);
  completionSelect.addEventListener('change', changeFilter);
  fromInput.addEventListener('change', changeFilter);
  toInput.addEventListener('change', changeFilter);
  searchInput.addEventListener('input', () => {
    search = searchInput.value.trim();
  });
  searchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') void refresh();
  });
  refreshButton.addEventListener('click', () => void refresh());
  highButton.addEventListener('click', () => {
    metric = 'highest';
    highButton.className = 'cms-button cms-button--primary';
    fastButton.className = 'cms-button cms-button--secondary';
    void refresh();
  });
  fastButton.addEventListener('click', () => {
    metric = 'fastest';
    fastButton.className = 'cms-button cms-button--primary';
    highButton.className = 'cms-button cms-button--secondary';
    void refresh();
  });

  const view = el(
    'div',
    { class: 'cms-view' },
    el(
      'div',
      { class: 'cms-view-heading' },
      el('div', {}, el('h1', { text: 'Dashboard' }), el('p', { text: 'Participation, completion, best attempts and every retained play session.' })),
      el(
        'div',
        { class: 'cms-view-actions' },
        button('Export matrix', 'secondary', () => void client.downloadReport(filters(), 'school')),
        button('Export attempts', 'primary', () => void client.downloadReport(filters(), 'attempts'))
      )
    ),
    el(
      'div',
      { class: 'cms-filter-bar' },
      el('label', { class: 'cms-search-field' }, searchInput),
      classSelect,
      gameSelect,
      completionSelect,
      el('label', { class: 'cms-date-field' }, el('span', { text: 'From' }), fromInput),
      el('label', { class: 'cms-date-field' }, el('span', { text: 'To' }), toInput),
      refreshButton
    ),
    summary,
    participation,
    el(
      'section',
      { class: 'cms-panel' },
      el('div', { class: 'cms-panel__header' }, el('div', {}, el('h2', { text: 'Student progress' }), el('p', { text: 'Best score, duration, attempt count and last played date' })), el('span', { class: 'cms-best-rule', text: 'Score ↓  Time ↑  Earliest wins' })),
      matrix
    ),
    el(
      'section',
      { class: 'cms-panel' },
      el('div', { class: 'cms-panel__header' }, el('div', {}, el('h2', { text: 'Leaderboards' }), el('p', { text: 'One completed entry per student' })), el('div', { class: 'cms-segmented' }, highButton, fastButton, button('Export', 'quiet', () => void client.downloadReport(filters(), 'leaderboard', metric)))),
      leaderboards
    )
  );
  await refresh(true);
  return view;
}
